import chroma from "chroma-js";
import type { StylesConfig } from "react-select";

import type { SelectOption } from "@winshop-crm/types";

const background = "white";
const color = "rgba(31, 41, 55, 1)";
const backgroundHover = "#a5a5a590";
const backgroundSelected = "#E2E8F0";
const borderRadius = "0.5rem";
const chipColor = "#075E9D";

const borderColor = (invalid: boolean) =>
  invalid ? "rgba(252, 165, 165, 1)" : "rgba(209, 213, 219, 1)";

/** Styles shared by single + multi react-select (moved from Common/select-autocomplete). */
const base = <IsMulti extends boolean>(invalid: boolean): StylesConfig<SelectOption, IsMulti> => ({
  container: (styles) => ({
    ...styles,
    transition: "all 0.2s",
    borderRadius,
    border: "1px solid",
    borderColor: borderColor(invalid),
    boxShadow: "0 0 rgba(0,0,0,0), 0 0 rgba(0,0,0,0), 0 1px 2px 0 rgba(0, 0, 0, 0.05)",
    lineHeight: "1.25rem",
    fontSize: "0.875rem",
    margin: "0px",
    padding: "0px",
  }),
  valueContainer: (styles) => ({ ...styles, margin: "0", fontSize: "0.875rem" }),
  input: (styles) => ({ ...styles, border: "none", outline: "none" }),
  indicatorsContainer: (styles) => ({ ...styles, padding: "1px" }),
  singleValue: (styles) => ({ ...styles, color }),
  control: (styles) => ({
    ...styles,
    border: "none",
    background,
    borderRadius,
    fontSize: "0.875rem",
    color,
    margin: "0",
    padding: "0px",
    minHeight: "30px",
  }),
  menu: (styles) => ({
    ...styles,
    minHeight: "34px",
    background,
    borderRadius,
    padding: "4px",
    "*::-webkit-scrollbar": { width: "12px" },
    "*::-webkit-scrollbar-track": { margin: "6px" },
    "*::-webkit-scrollbar-thumb": {
      backgroundColor: "rgba(0,0,0,.1)",
      pointer: "cursor",
      borderRadius,
    },
  }),
  menuList: (styles) => ({ ...styles, minHeight: "34px", background, borderRadius }),
  option: (styles, { data, isDisabled, isFocused, isSelected }) => ({
    ...styles,
    padding: "6px 10px",
    borderRadius,
    width: "99%",
    margin: "auto",
    backgroundColor: isSelected ? backgroundSelected : isFocused ? backgroundHover : undefined,
    color: isDisabled
      ? "#ccc"
      : isSelected
        ? chroma.contrast(backgroundSelected, "white") > 2
          ? "white"
          : "black"
        : data.color
          ? chroma(data.color).darken(1).css()
          : undefined,
    cursor: isDisabled ? "not-allowed" : "default",
    ":active": {
      ...styles[":active"],
      backgroundColor: isSelected ? "pink" : "transparent",
    },
  }),
});

export const singleSelectStyles = (invalid: boolean): StylesConfig<SelectOption, false> =>
  base<false>(invalid);

export const multiSelectStyles = (invalid: boolean): StylesConfig<SelectOption, true> => ({
  ...base<true>(invalid),
  indicatorsContainer: (styles) => ({ ...styles, height: "32px", padding: "1px" }),
  multiValue: (styles, { data }) => ({
    ...styles,
    backgroundColor: chroma(data.color || chipColor)
      .brighten(1)
      .alpha(0.1)
      .css(),
    borderRadius: "0.5rem",
  }),
  multiValueLabel: (styles, { data }) => ({
    ...styles,
    color: chroma(data.color || chipColor)
      .darken(2)
      .css(),
  }),
  multiValueRemove: (styles, { data }) => ({
    ...styles,
    color: chroma(data.color || chipColor)
      .darken(2)
      .css(),
    marginLeft: "4px",
    ":hover": {
      backgroundColor: data.color || chipColor,
      borderRadius: "0.5rem",
      color: "white",
    },
  }),
});
